import * as React from 'react';

import { textContainerClassName } from '@1js/acui-common';

import {
  useAriaKeyShortcuts,
  useAriaLabel,
  useDataAutomationType,
  useDataNav,
  useDataUniqueId,
  useFunctionGetInputMethod,
  useShorthandFontIcon,
} from '../../../hooks';
import { AppButtonProps, AppButtonState } from './AppButton.types';

export const useAppButtonState = (props: AppButtonProps, state: AppButtonState): AppButtonState => {
  const {
    dataAutomationType,
    dataNav,
    fullDescriptionLabel,
    id,
    keyShortcuts,
    label,
    location,
    onExecute,
    onMouseDown,
    shouldTakeFocus,
    takeFocusOnClick,
    hideLabel,
    toggled = false,
  } = props;

  // Icon only considerations
  const iconOnly = state.iconOnly || !!hideLabel;
  state.iconOnly = iconOnly;
  state.root.children = !iconOnly
    ? state.root.children || <span className={textContainerClassName}>{label}</span>
    : undefined;

  useAriaLabel(state, fullDescriptionLabel || label);
  useAriaKeyShortcuts(state, keyShortcuts);
  useDataAutomationType(state, dataAutomationType ?? 'AppButton');
  useDataUniqueId(state, id, location);
  useDataNav(state, dataNav);
  useShorthandFontIcon(state);
  const getInputMethod = useFunctionGetInputMethod(state);

  state.root.onClick = React.useCallback(
    (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onExecute) {
        onExecute({
          id,
          location,
          toggled,
          inputMethod: getInputMethod(ev),
          shouldTakeFocus,
          triggerTime: ev && ev.timeStamp,
        });
      }
    },
    [getInputMethod, id, location, onExecute, shouldTakeFocus, toggled]
  );

  state.root.onMouseDown = React.useCallback(
    (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onMouseDown) {
        onMouseDown(ev);
      }
      if (!takeFocusOnClick) {
        ev.preventDefault();
      }
    },
    [onMouseDown, takeFocusOnClick]
  );

  return state;
};
